// Elova — storefront route guard for Shopify's native checkout.
// Loaded storefront-wide from snippets/elova-checkout-guard.liquid (rendered
// by blocks/elova-footer-logo.liquid), NOT from layout/theme.liquid, which the
// Yampi app can rewrite.
//
// WHY: Yampi owns checkout. Any link or form on a theme-rendered page that
// reaches Shopify's native checkout bypasses Yampi, Appmax's installment
// table, Melhor Envio and the NF-e flow. The theme ships no such link today;
// this catches the ones an app, a rich-text field or a future edit adds.
// Everything is sent to the cart page instead, where Yampi takes over.
//
// WHAT IT CANNOT DO: requests that never render a theme page — a typed or
// bookmarked /checkout, a cart permalink opened from outside the site,
// Shopify-generated emails — are 302'd by Shopify's server before any theme
// code runs. Those are covered by detection (TASKS.md go-live gate), not
// by this file.
//
// Yampi's own `/a/checkout` links are deliberately NOT matched.

const CART_URL = '/cart';
const PERMALINK = /^\/cart\/(\d+:\d+(?:,\d+:\d+)*)\/?$/;
const NATIVE_CHECKOUT = /^\/(checkout|checkouts)(\/|$)|^\/cart\/checkout\/?$/;

function sameOrigin(url) {
  return url.origin === window.location.origin;
}

function toUrl(value) {
  try {
    return new URL(value, window.location.href);
  } catch {
    return null;
  }
}

// On the cart page, hand over to Yampi's own button if it is there;
// anywhere else (or if it is not), go to the cart page.
function goToYampi() {
  const yampiButton = document.getElementById('checkoutympButton');
  if (yampiButton && window.location.pathname === CART_URL) {
    yampiButton.click();
    return;
  }
  window.location.assign(CART_URL);
}

// A cart permalink (/cart/<variant>:<qty>,…) carries items; keep them by
// adding them to the cart before handing over.
async function followPermalink(match) {
  const items = match[1].split(',').map((pair) => {
    const [id, quantity] = pair.split(':').map(Number);
    return { id, quantity };
  });
  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
  } finally {
    window.location.assign(CART_URL);
  }
}

document.addEventListener(
  'click',
  (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    const url = toUrl(link.getAttribute('href'));
    if (!url || !sameOrigin(url)) return;

    const permalink = url.pathname.match(PERMALINK);
    if (permalink) {
      event.preventDefault();
      event.stopImmediatePropagation();
      followPermalink(permalink);
      return;
    }
    if (NATIVE_CHECKOUT.test(url.pathname)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      goToYampi();
    }
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    const action = toUrl(submitter?.getAttribute('formaction') || form.getAttribute('action') || '');

    // A form carries `checkout` either on its submitter or as a plain field
    // (e.g. a hidden input). Disabled controls are never submitted, which is
    // why the cart page's permanently disabled #checkout does not count.
    const checkoutSubmit =
      submitter?.getAttribute('name') === 'checkout' ||
      Array.from(form.elements).some(
        (el) => el.name === 'checkout' && !el.disabled && el !== submitter && !['submit', 'button', 'image'].includes(el.type)
      );
    const checkoutAction = action && sameOrigin(action) && NATIVE_CHECKOUT.test(action.pathname);
    if (!checkoutSubmit && !checkoutAction) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    goToYampi();
  },
  true
);
